import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { motion } from 'framer-motion';

const LearnMorePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: "Smart Ingredient Analysis",
      description: "Our proprietary neural network decodes complex product formulas, cross-referencing over 12,000 unique ingredients to determine exactly how they interact with your skin's biological markers.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="3" strokeWidth="1.5" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-4.35-4.35M12 8V4m0 16v-4m4-4h4m-16 0h4m9.192-4.192l-2.828 2.828M5.636 18.364l2.828-2.828M18.364 18.364l-2.828-2.828M5.636 5.636l2.828 2.828" />
        </svg>
      )
    },
    {
      title: "Bio-Digital Skin Passport",
      description: "We create a high-fidelity digital twin of your skin profile. By tracking hydration levels, sensitivity patterns, and age-related goals, we ensure your routine evolves alongside your life.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
        </svg>
      )
    },
    {
      title: "Autonomous Recommendation Engine",
      description: "Powered by scientific consensus and real-time user feedback, our AI filters through a library of 39,000+ products to curate a selection that minimizes waste and maximizes results.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
        </svg>
      )
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-[#7A6E62]">
      <Header />
      
      <main className="flex-grow z-10 relative">
        {/* Simplified Modern Hero Section */}
        <section className="relative px-4 pt-16 pb-12 md:pt-24 md:pb-24 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl"
          >
            <h1 className="text-4xl md:text-7xl font-extrabold tracking-tight text-white mb-6 leading-tight">
              Decoding the <span className="text-[#E9A969]">Future</span> of Your Skin
            </h1>
            <p className="text-lg md:text-2xl text-white/80 font-normal leading-relaxed max-w-2xl mx-auto">
              Scientific precision meets personalized beauty. Explore the technology that makes Cosmelytix unique.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="mt-8"
          >
            <div className="w-px h-16 bg-white/30"></div>
          </motion.div>
        </section>

        {/* Feature Grid - Refined Glassmorphism */}
        <section className="px-4 py-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group p-8 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/15 transition-all shadow-xl"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#E9A969] flex items-center justify-center text-[#5F40A1] mb-6 shadow-lg group-hover:rotate-6 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-white/70 leading-relaxed text-base">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Consistent CTA Section */}
        <section className="px-4 py-24 mb-12">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-10 md:p-16 rounded-3xl bg-[#EDEAF4] shadow-2xl"
            >
              <h2 className="text-3xl md:text-5xl font-bold text-[#5F40A1] mb-6">Ready to see the science?</h2>
              <p className="text-lg text-gray-700 mb-10 max-w-xl mx-auto font-medium">
                Join thousands of users who trust our AI to curate their perfect skincare ritual.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button 
                  onClick={() => navigate('/products')}
                  className="px-10 py-3.5 rounded-full bg-[#5F40A1] text-white font-bold text-lg shadow-lg hover:bg-[#4A2F82] transition-all transform hover:-translate-y-0.5"
                >
                  Explore Products
                </button>
                <button 
                  onClick={() => navigate('/prices')}
                  className="px-10 py-3.5 rounded-full border-2 border-[#5F40A1] text-[#5F40A1] font-bold text-lg hover:bg-[#5F40A1] hover:text-white transition-all transform hover:-translate-y-0.5"
                >
                  View Plans
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LearnMorePage;
